import {
  Component,
  effect,
  inject,
  model,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { ProductoApiService } from '@core/services/api/producto.service';
import { InputBuscador } from '@shared/components/input-buscador/input-buscador';
import { ModalConfirmacion } from '@shared/components/modal-confirmacion/modal-confirmacion';
import { Paginacion } from '@shared/components/paginacion/paginacion';
import { Tabla } from '@shared/components/tabla/tabla';
import { CrearProducto } from '@shared/components/crear-producto/crear-producto';
import { EditarProducto } from '@shared/components/editar-producto/editar-producto';
import { ToastService } from '@shared/services/toast.service';
import { CategoriaApiService } from '@core/services/api/categoria-api.service';
import { ICategoriaData } from '@core/interfaces/entidades/categoria.entidad';
import { VerProducto } from '@shared/components/ver-producto/ver-producto';
import { CommonModule, CurrencyPipe, UpperCasePipe } from '@angular/common';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import { AppService } from '@shared/services/app.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { ETipoProducto } from '@core/enums/tipoProducto.enum';
import { TipoUnidadSunatPipe } from '@shared/pipes/tipo-unidad-sunat.pipe';
import { UiButton, UiDialog, UiSelect, UiTooltip } from '@shared/components/ui';
import { EtiquetaProductoService } from '@shared/services/etiqueta-producto.service';

@Component({
  selector: 'app-producto',
  imports: [
    CommonModule,
    InputBuscador,
    Tabla,
    Paginacion,
    ModalConfirmacion,
    CrearProducto,
    EditarProducto,
    VerProducto,
    CurrencyPipe,
    UpperCasePipe,
    UiButton,
    UiDialog,
    UiSelect,
    UiTooltip,
  ],
  templateUrl: './producto.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './producto.css',
})
export class Producto implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cambiarPagina(1);
        this.categoriaApiService.cargar().subscribe({
          next: (response) => {
            this.categorias.set(response);
          },
          error: (err) => {
            if (err.status == 401) return;
            this.toastService.show(
              'top-right',
              'error',
              'Error',
              err.error.message ?? 'No se pudo cargar la lista de categorías.',
            );
          },
        });
      }
    });
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Catálogo de Productos',
      subtitle: 'Administra tu catálogo, categorías y stock por sucursal.',
      showBackButton: true,
      icon: 'bi bi-box-seam',
    });
    this.columnasTabla = [
      { key: 'codigo', titulo: 'Código' },
      { key: 'nombre', titulo: 'Nombre' },
      {
        key: 'url_imagen',
        titulo: 'Imagen',
        type: 'image',
        imageKey: 'url_imagen',
        fallbackIcon: 'bi bi-box',
      },
      { key: 'categoria', titulo: 'Categoría', template: this.categoriaTemplate() },
      { key: 'precio_venta', titulo: 'Precio de Venta', template: this.precioTemplate() },
      { key: 'precio_base', titulo: 'Precio Base', template: this.precioBaseTemplate() },
      { key: 'producto_servicio', titulo: 'Tipo', template: this.tipoTemplate() },
      { key: 'stock', titulo: 'Stock', template: this.stockTemplate() },
      { key: 'unidad_medida', titulo: 'Unidad', pipe: new TipoUnidadSunatPipe() },
      { key: 'activo', titulo: 'Estado', template: this.activoTemplate() },
    ];
  }
  // views
  stockTemplate = viewChild('stockTemplate');
  tipoTemplate = viewChild('tipoTemplate');
  precioTemplate = viewChild('precioTemplate');
  precioBaseTemplate = viewChild('precioBaseTemplate');
  categoriaTemplate = viewChild('categoriaTemplate');
  activoTemplate = viewChild('activoTemplate');
  // servicios
  private productoApiService = inject(ProductoApiService);
  private categoriaApiService = inject(CategoriaApiService);
  private toastService = inject(ToastService);
  private appService = inject(AppService);
  private sucursalService = inject(SucursalService);
  private etiquetaProductoService = inject(EtiquetaProductoService);
  // datos
  productoSeleccionado = signal<IProductoData | undefined>(undefined);
  private itemsPorPagina = 18;
  busqueda = model<string>('');
  modal_ver = model<boolean>(false);
  modal_crear = model<boolean>(false);
  modal_editar = model<boolean>(false);
  modal_eliminar = model<boolean>(false);
  paginacion = signal<IPaginar<IProductoData> | null>(null);
  categorias = signal<ICategoriaData[]>([]);
  categoria_seleccionada = model<number | null>(null);
  tipo_producto_seleccionado = model<ETipoProducto | null>(null);
  opcionesTipoProducto = [
    { label: 'Todos', value: null },
    { label: 'Producto', value: ETipoProducto.PRODUCTO },
    { label: 'Ingrediente', value: ETipoProducto.INGREDIENTE },
    { label: 'Receta', value: ETipoProducto.RECETA },
  ];

  columnasTabla!: ITablaColumna[];

  // métodos
  onBuscar() {
    if (this.busqueda().trim() === '') {
      this.cambiarPagina(1);
      return;
    }
    this.productoApiService.buscarProducto(this.busqueda()).subscribe((response) => {
      this.paginacion.set({
        data: response.data,
        total: response.data.length,
        size: response.data.length,
        pagina: 1,
      });
    });
  }
  onVer(producto: IProductoData) {
    this.modal_ver.set(true);
    this.productoSeleccionado.set(producto);
  }
  async imprimirEtiqueta(producto: IProductoData) {
    const generada = await this.etiquetaProductoService.imprimir(producto);

    if (!generada) {
      this.toastService.show(
        'top-right',
        'error',
        'No se pudo imprimir la etiqueta',
        'El código del producto no es válido para Code128 o el navegador bloqueó la ventana de impresión.',
      );
    }
  }
  onCrear(producto?: IProductoData) {
    this.modal_crear.set(false);
    if (producto) {
      this.paginacion.update((paginacion) => {
        paginacion?.data.unshift(producto);
        paginacion!.total += 1;
        return { ...paginacion! };
      });
    }
  }
  onEditar(producto: IProductoData) {
    this.modal_editar.set(true);
    this.productoSeleccionado.set(producto);
  }
  onCerrarEditar(producto?: IProductoData) {
    this.modal_editar.set(false);
    this.productoSeleccionado.set(undefined);
    if (producto) {
      const datosActualizados = this.paginacion()?.data.map((p) =>
        p.id === producto.id ? producto : p,
      );
      this.paginacion.set({
        data: datosActualizados || [],
        total: this.paginacion()?.total || 0,
        pagina: this.paginacion()?.pagina || 1,
        size: this.paginacion()?.size || this.itemsPorPagina,
      });
    }
  }
  productoAEliminar = signal<IProductoData | undefined>(undefined);
  solicitarEliminar(producto: IProductoData) {
    this.productoAEliminar.set(producto);
    this.modal_eliminar.set(true);
  }
  eliminar() {
    const producto = this.productoAEliminar();
    if (!producto) {
      return;
    }
    this.productoApiService.eliminar(producto.codigo).subscribe({
      next: (rs) => {
        this.toastService.show('top-right', 'success', 'Éxito', rs.message);
        this.paginacion.update((paginacion) => {
          const datosActualizados = paginacion?.data.filter((p) => p.id !== producto.id) || [];
          return {
            data: datosActualizados,
            total: (paginacion?.total || 1) - 1,
            pagina: paginacion?.pagina || 1,
            size: paginacion?.size || this.itemsPorPagina,
          };
        });
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message ?? 'No se pudo eliminar el producto.',
        );
      },
      complete: () => {
        this.modal_eliminar.set(false);
        this.productoAEliminar.set(undefined);
      },
    });
  }
  cerrarModalEliminar() {
    this.modal_eliminar.set(false);
  }

  cambiarPagina(nuevaPagina: number) {
    this.productoApiService
      .paginar(
        nuevaPagina,
        this.itemsPorPagina,
        this.categoria_seleccionada() ?? undefined,
        this.tipo_producto_seleccionado() ?? undefined,
      )
      .subscribe({
        next: (response) => {
          this.paginacion.set(response);
        },
        error: (err) => {
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error.message ?? 'No se pudo cargar la lista de productos.',
          );
        },
      });
  }
  filtrarPorCategoria(categoriaId: number | null): void {
    this.categoria_seleccionada.set(categoriaId);
    this.cambiarPagina(1);
  }

  filtrarPorTipoProducto(tipo: ETipoProducto | null): void {
    this.tipo_producto_seleccionado.set(tipo);
    this.cambiarPagina(1);
  }
}
